import { useRef } from "react"

import { HelpCenterContent, HelpCenterHeader, WikiSection, DownloadCard } from "@/areas/help-center"
import Article from "@/shared/layouts/Article/Article"

function LinkingOracleSetup() {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        title="OCB Desktop App Setup"
        subtitle="Required Oracle POS configurations to ensure correct order processing."
        logo="/static/wiki/oracle/oracle-logo.png"
        backPath="/help-center/linking-pos-devices"
        backLabel="Linking POS Devices"
        contentRef={contentRef}
      />

      <WikiSection id="overview" title="OVERVIEW">
        <Article>
          <p>The following process is going to explain step by:</p>
          <ul className="wiki-list">
            <li>Update the OCB app using the correct version;</li>
            <li>Creating the Station to be used by Stream;</li>
            <li>Connecting and Configuring the POS inside Oracle Databases.</li>
          </ul>

          <h3>Requirements</h3>
          <p>To successfully complete this setup, you will need:</p>
          <ul className="wiki-list">
            <li><strong>EMC</strong> access with the necessary permissions;</li>
            <li>The <strong>ScreenId</strong> to be used by the OCB App, for you to download it correctly.</li>
          </ul>
        </Article>
      </WikiSection>

      <WikiSection id="download-install-ocb" title="A - DOWNLOAD AND INSTALL THE OCB DESKTOP APP">
        <Article>
          <p>In our Download Hub, you'll find a variety of essential downloads. After downloading the OCB app, extract the files, run the .exe installer, and complete the installation steps.</p>

          <DownloadCard
            name="OCB App"
            version="v.0.41.2"
            releaseDate="Month, DD, YYYY"
            downloadUrl="#"
          />

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After downloading, extract the files, run the <strong>.exe installer</strong>, and complete the installation steps.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once installed, locate the OCB app on your computer and launch it.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>The application will automatically start loading and preparing for use.</p>
          </div>

          <p>See below for a visual representation of the OCB App loading screen.</p>
        </Article>
      </WikiSection>

      <WikiSection id="open-access-configuration" title="B — OPEN THE OCB APP AND ACCESS THE CONFIGURATION FLOW">
        <Article>
          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open the <strong>OCB App</strong> on your desktop.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You will be presented with the main screen. From here, you can access the application settings and configuration panel.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>If this is your first time using the app, you may be prompted to configure your screen ID.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Back to the main screen, click on the settings icon or open the configuration panel to begin the setup process.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-01.png" alt="OCB App welcome screen" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click on the <strong>Settings</strong> icon.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>The settings panel will display on the left with various options such as <strong>Text Settings</strong>, <strong>Background Settings</strong>, and <strong>Other Settings</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-02.png" alt="OCB settings panel" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Navigate to the <strong>password</strong> field.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Enter the password provided by the Stream team to proceed.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once authenticated, you&apos;ll be able to access the configuration wizard to link your Oracle POS.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-03.png" alt="Password entry screen" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="configure-ocb-oracle" title="C - CONFIGURE OCB APP FOR ORACLE POS">
        <Article>
          <h3>Select Oracle Integration</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After entering your password, you will be directed to the integration wizard.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On the first step, you&apos;ll see a screen titled <strong>CHOOSE POINT OF SALES</strong>. This is where you select which POS system you want to integrate with.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click on the dropdown menu and select <strong>Oracle Integration</strong> from the list of available POS systems.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/linking-oracle-04.png" alt="Choose Point of Sales screen" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/linking-oracle-05.png" alt="POS dropdown menu expanded" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-06.png" alt="Oracle Integration selected" className="wiki-screenshot" />
          </div>

          <h3>Define Integration Parameters</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After selecting Oracle Integration, click the <strong>Next</strong> button to proceed to the configuration form.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On step 2, you will be presented with the <strong>CONFIGURATION FORM</strong> where you need to provide the following information:</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p><strong>orgShortName:</strong> Your organization&apos;s short name as registered in the Oracle POS system. This identifier helps the OCB app connect to the correct organizational account.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p><strong>socket:</strong> The location reference code for your specific Oracle POS location. This ensures the app communicates with the right physical location.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p><strong>token:</strong> A secure authentication token provided by the Stream team. This token authorizes the OCB app to access and interact with your Oracle POS data.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p><strong>Enable leaderboard:</strong> Toggle this option if you want to enable the leaderboard feature for your location.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After filling in all required fields, click the <strong>Next</strong> button to proceed.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/linking-oracle-07.png" alt="Configuration form step 2" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/oracle/linking-oracle-08.png" alt="Confirm your choices step 3" className="wiki-screenshot" />
          </div>

          <h3>Validate OCB App Status</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On the final step, you will see a <strong>CONFIRM YOUR CHOICES</strong> screen that displays all the configuration details you&apos;ve entered.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Review all the information carefully to ensure everything is correct. Check the integration name, organization short name, socket reference, and token.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once you&apos;ve verified all details, click the <strong>Confirm</strong> button to complete the setup.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-09.png" alt="Setup complete - Welcome screen" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default LinkingOracleSetup
